import { Button, Checkbox, Col, Form } from 'antd';
import React from 'react';
import {
  AppContent,
  ErrorHandler,
  Gutter,
  LoadingState,
  Title,
} from '@common/components';
import { useMutation, useQuery } from '@common/hooks';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { createMessage } from 'works/legacy/lib/message-helper';
import { ORGANIZATION_ADMIN_MENU } from 'works/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import {
  OrganizationOnDemandSettingsQuery,
  UpdateOnDemandSettings,
  WorksIntegrationConfigurationQuery,
} from '@scheduling/graphql';

type FormValues = {
  allowListEditing: boolean | undefined;
};

export function AdminAllowListsPage() {
  const [form] = Form.useForm<FormValues>();

  const {
    loading: integrationConfigLoading,
    data: integrationConfigData,
    error: integrationConfigError,
  } = useQuery(WorksIntegrationConfigurationQuery);

  const {
    loading: onDemandSettingsLoading,
    data: onDemandSettingsData,
    error: onDemandSettingsError,
  } = useQuery(OrganizationOnDemandSettingsQuery);

  const [updateSettings, { loading: updateLoading }] = useMutation(
    UpdateOnDemandSettings,
    {
      onCompleted: () => {
        createMessage('success', 'Allow lists editing settings updated');
      },
      onError: () => {
        createMessage('error', 'Failed to update allow lists editing settings');
      },
    }
  );

  useSetBreadcrumbs([
    {
      name: ORGANIZATION_ADMIN_MENU.HOME.LABEL,
      path: ORGANIZATION_ADMIN_MENU.HOME.ROUTE,
    },
    {
      name: ORGANIZATION_ADMIN_MENU.ALLOW_LISTS.LABEL,
      path: ORGANIZATION_ADMIN_MENU.ALLOW_LISTS.ROUTE,
    },
  ]);

  const allowListSyncEnabled =
    integrationConfigData?.integrationConfiguration?.allowListSyncEnabled;
  const disabled = !allowListSyncEnabled;
  const checked =
    onDemandSettingsData?.organizationOnDemandSettings
      ?.allowListsEditWithSync || disabled;

  const onFinish = async (values: FormValues) => {
    if (disabled) return;

    updateSettings({
      variables: {
        input: {
          allowListsEditWithSync: values.allowListEditing || false,
        },
      },
    });
  };

  if (integrationConfigLoading || onDemandSettingsLoading) {
    return <LoadingState />;
  }

  const error = integrationConfigError || onDemandSettingsError;

  if (error) {
    return <ErrorHandler error={error} log="AdminAllowListsPage" />;
  }

  return (
    <>
      <AppContent>
        <Gutter direction="bottom" size="lg">
          <Title size="h4" weight="bold">
            Allow Lists Editing
          </Title>
        </Gutter>
        <Form
          onFinish={onFinish}
          layout="vertical"
          form={form}
          initialValues={{ allowListEditing: checked }}
        >
          <Col>
            <Form.Item name="allowListEditing" valuePropName="checked">
              <Checkbox id="allowListEditing" disabled={disabled}>
                Allow managers to edit staff allow list units in Works
              </Checkbox>
            </Form.Item>
          </Col>
          {!disabled && (
            <Col>
              <Form.Item>
                <Button
                  type="primary"
                  size="large"
                  onClick={form.submit}
                  loading={updateLoading}
                >
                  Save
                </Button>
              </Form.Item>
            </Col>
          )}
        </Form>
      </AppContent>
    </>
  );
}
